import { createContext, useContext, useRef, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { updateWorkByItem, addWorkItem, getWorkByItem } from "../../api/work";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// import { useSearchParams } from "react-router-dom";

const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const navigate = useNavigate();

  const AddWorkArray = useRef();
  const AddWorkVideosArray = useRef();
  const AddWorkReelsArray = useRef();
  const AddWorkPortfolioArray = useRef();
  const toaster = useToaster();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemId = searchParams.get("itemId");

  const getWorkQuery = useQuery({
    queryKey: ["works", id, itemId],
    queryFn: getWorkByItem,
    enabled: !!itemId,
  });

  const updateWorkQuery = useMutation({ mutationFn: updateWorkByItem });
  const addWorkItemQuery = useMutation({ mutationFn: addWorkItem });

  const initialValues = useMemo(() => {
    const workAddData = getWorkQuery?.data?.data || {};
    return {
      works: {
        meta_title: workAddData?.meta_title || "",
        slug_url: workAddData?.slug_url || "",
        meta_keywords: workAddData?.meta_keywords || "",
        meta_description: workAddData?.meta_description || "",

        title: workAddData?.title || "",
        sub_heading: workAddData?.sub_heading || "",
        paragraph_info: workAddData?.paragraph_info || "",
        banner_image: workAddData?.banner_image || "",
        is_publish: workAddData?.is_publish || false,
        color: workAddData?.color || "",
        keywords: Array.isArray(workAddData?.keywords) ? workAddData.keywords.join(", ") : (workAddData?.keywords || ""),
        description: workAddData?.description || "",
        images: workAddData?.images || [{ image_link: "" }],
        task_heading: workAddData?.task_heading || "The Challenge",
        task_desc: workAddData?.task_desc || "",
        role_label: workAddData?.role_label || "Our Approach",
        role_desc: workAddData?.role_desc || "",
        solution_heading: workAddData?.solution_heading || "The Solution",
        solution_desc: workAddData?.solution_desc || "",

        stats: workAddData?.stats && workAddData.stats.length > 0 ? workAddData.stats : [
          { value: "3.2x", label: "INCREASE IN DAILY ACTIVE USERS" },
          { value: "-58%", label: "DROP IN ONBOARDING DROP-OFF RATE" },
          { value: "94%", label: "RETENTION RATE WITHIN THE FIRST 30 DAYS" }
        ],

        before_after: {
          badge: workAddData?.before_after?.badge || "The Transformation",
          before_title: workAddData?.before_after?.before_title || "BEFORE",
          before_desc: workAddData?.before_after?.before_desc || "",
          after_title: workAddData?.before_after?.after_title || "AFTER",
          after_desc: workAddData?.before_after?.after_desc || ""
        },

        outcome_desc: workAddData?.outcome_desc || "",

        client_feedback: {
          quote: workAddData?.client_feedback?.quote || "",
          author: workAddData?.client_feedback?.author || ""
        },

        client_label: workAddData?.client_label || "",
        client_desc: workAddData?.client_desc || "",
        category_label: workAddData?.category_label || "",
        category_desc: workAddData?.category_desc || "",
        site_link: workAddData?.site_link || "",
        order_index: workAddData?.order_index !== undefined ? workAddData.order_index : "",

        multiple_images: workAddData?.multiple_images || [{ multi_image_link: "" }],
        reels: workAddData?.reels || [{ reel_image_link: "" }],
        portfolio_images: workAddData?.portfolio_images || [{ portfolio_image_link: "" }],
      },
    };
  }, [getWorkQuery]);

  const handleSubmit = async (values) => {
    const payload = {
      id,
      meta_title: values?.works?.meta_title || "",
      meta_keywords: values?.works?.meta_keywords || "",
      meta_description: values?.works?.meta_description || "",
      slug_url: values?.works?.slug_url || "",

      title: values?.works?.title || "",
      sub_heading: values?.works?.sub_heading || "",
      paragraph_info: values?.works?.paragraph_info || "",
      banner_image: values?.works?.banner_image || "",
      color: values?.works?.color || "",
      is_publish: values?.works?.is_publish,
      description: values?.works?.description || "",
      keywords: values?.works?.keywords ? values.works.keywords.split(",").map((k) => k.trim()) : [],
      images: values.works.images,
      multiple_images: values.works.multiple_images,
      reels: values.works.reels,
      portfolio_images: values.works.portfolio_images,
      task_heading: values?.works?.task_heading || "",
      task_desc: values?.works?.task_desc || "",
      role_label: values?.works?.role_label || "",
      role_desc: values?.works?.role_desc || "",
      solution_heading: values?.works?.solution_heading || "",
      solution_desc: values?.works?.solution_desc || "",
      stats: values?.works?.stats || [],
      before_after: values?.works?.before_after || {},
      outcome_desc: values?.works?.outcome_desc || "",
      client_feedback: values?.works?.client_feedback || {},
      client_label: values?.works?.client_label || "",
      client_desc: values?.works?.client_desc || "",
      category_label: values?.works?.category_label || "",
      category_desc: values?.works?.category_desc || "",
      site_link: values?.works?.site_link || "",
      order_index: values?.works?.order_index !== "" && values?.works?.order_index !== undefined ? Number(values.works.order_index) : 0,
    };
    const onSuccessCallback = (res) => {
      toaster.success(Const.MESSAGES.SUCCESS, Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY);

      const newItemId = res.data.works.find((item) => {
        return item.title === getWorkQuery?.data?.data.title;
      });

      if (newItemId?._id) {
        searchParams.set("itemId", newItemId._id);
        setSearchParams(searchParams);
      }
    };

    if (itemId) {
      payload.itemId = itemId;
      updateWorkQuery.mutate(payload, {
        onSuccess: onSuccessCallback,
      });
      return;
    }

    addWorkItemQuery.mutate(payload, {
      onSuccess: onSuccessCallback,
    });
  };

  const handleNewWorkAdd = () => {
    AddWorkArray.current.push({ image_link: "" });
  };

  const handleNewWorkDelete = (index) => () => {
    AddWorkArray.current.remove(index);
  };

  const handleNewWorkVideosAdd = () => {
    AddWorkVideosArray.current.push({ multi_image_link: "" });
  };

  const handleNewWorkReelsAdd = () => {
    AddWorkReelsArray.current.push({ reel_image_link: "" });
  };

  const handleNewWorkProtfolioAdd = () => {
    AddWorkPortfolioArray.current.push({ portfolio_image_link: "" });
  };

  return (
    <PageContext.Provider
      value={{
        initialValues,
        handleNewWorkVideosAdd,
        handleNewWorkReelsAdd,
        handleNewWorkProtfolioAdd,
        handleNewWorkDelete,
        AddWorkArray,
        handleNewWorkAdd,
        getWorkQuery,
        handleSubmit,
        AddWorkVideosArray,
        AddWorkReelsArray,
        AddWorkPortfolioArray,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const useAddWorkProvider = () => {
  return useContext(PageContext);
};
